import { gqlFetch } from '@/service/gql'
import { gql, createAuthScopedMemo } from '@amzn/bf-playback'
import { getAuth, notSignedIn, signedIn } from '@/auth/authRef'
import { combineData, externalData, type ObservableValue } from '@amazon/vinyl-observable'
import { createLogPrefix, logWarn, type Maybe } from '@amazon/vinyl-util'

// Child-profile gate — the shared foundation for GH's age-band feature gates
// (create-public-playlist, third-party sharing, explicit-language filter).
// Mirrors GH's `isChildProfile` (`profile.profileType === 'CHILD'`) and the
// `services/capabilities/*` idiom, exposed the same way as the sibling
// `canChangeSubscription` gate. CHILD is the only age band detectable
// client-side; TEEN is server-only and never synthesized here.

const target = { logPrefix: createLogPrefix('childProfile') }

/** Whether the active profile's type denotes a child profile. */
export function isChildProfile(profileType: Maybe<string>): boolean {
    return profileType === 'CHILD'
}

// The active-profile `profileType` GH reads. amu's active profile is the
// singular `profile` field (the profiles list uses `profiles(...)`), matching
// what `canChangeSubscription` already reads.
const profileTypeQuery = gql(`
    query childProfileType {
        profile {
            id
            profileType
        }
    }
`)

const memo = createAuthScopedMemo(getAuth, () =>
    gqlFetch(profileTypeQuery, {}).then((res) => res.profile)
)

/** The active profile's id, from the same memoized probe as its age band. */
export function loadActiveProfileId(): Promise<Maybe<string>> {
    return memo.get().then((profile) => profile?.id)
}

/**
 * Fetch (and memoize) the active profile's type. The memo is dropped on
 * sign-out so a later sign-in re-reads the new profile's age band (mirrors
 * {@link loadChangeSubscriptionCapability}).
 */
export function loadProfileType(): Promise<Maybe<string>> {
    // A rejected probe is not cached (see createAuthScopedMemo), so a transient
    // error can't over-restrict a resolved adult for the session. Child-safety
    // direction is unaffected: the gates stay fail-safe until a probe succeeds.
    return memo.get().then((profile) => profile?.profileType)
}

// `null` until the active profile resolves (signed out / auth-unknown /
// probe-failed). The restriction gates below treat `null` as unresolved and
// fail SAFE (restricted) so a child never briefly sees a public affordance
// during the profile fetch; the positive `isChildProfile$` reads `null` as
// "not yet known to be a child" (false).
const profileType$: ObservableValue<Maybe<string>> = externalData<Maybe<string>>(null, (setData) =>
    signedIn().onData((isIn) => {
        if (!isIn) {
            setData(null)
            return
        }
        loadProfileType()
            .then(setData)
            .catch((err) => {
                // A probe failure leaves the type unresolved — the
                // restriction gates stay fail-safe (hidden) rather than
                // assuming adult on a failed read.
                logWarn(target, 'profileType probe failed', err)
                setData(null)
            })
    })
)

/** Whether the active profile is (resolved to be) a child profile. */
export const isChildProfile$: ObservableValue<boolean> = profileType$.map(isChildProfile)

/**
 * One-shot confirmed-child check for the route guard: resolves `true` only when
 * the signed-in profile is KNOWN to be a child. Signed-out or a failed probe
 * resolves `false`, so a non-child is never restricted. Shares the memoized
 * {@link loadProfileType} probe with the nav gate — no second fetch.
 */
export function loadIsChildProfile(): Promise<boolean> {
    if (!getAuth().isSignedIn()) return Promise.resolve(false)
    return loadProfileType().then(isChildProfile, () => false)
}

/**
 * R23 — create-public-playlist gate. GH: `canCreatePublicPlaylists =
 * !isChildProfile`. amu additionally fails SAFE until the profile resolves:
 * a public affordance appears only once the profile is known to be non-child.
 */
export const canCreatePublicPlaylists$: ObservableValue<boolean> = profileType$.map(
    (t) => t != null && !isChildProfile(t)
)

const signedOut$: ObservableValue<boolean> = externalData(false, (setData) =>
    notSignedIn().onData(setData)
)

/**
 * Third-party-sharing gate. GH: `canUseThirdPartySharing = !isChildProfile`, so a signed-out
 * visitor (no profile) is not a child; a signed-in profile stays hidden until it resolves.
 */
export const canUseThirdPartySharing$: ObservableValue<boolean> = combineData({
    signedOut: signedOut$,
    profileType: profileType$,
}).map(
    ({ signedOut, profileType }) =>
        signedOut || (profileType != null && !isChildProfile(profileType))
)

/**
 * Whether the Podcasts + Audiobooks nav ingresses may be shown. GH withholds
 * them in Kids Mode (`!isChildProfile`), so a signed-out visitor sees them; a
 * signed-in profile stays hidden until it resolves so a loading child never
 * glimpses an ingress.
 */
export const canAccessPodcastsAndAudiobooks$: ObservableValue<boolean> = combineData({
    signedOut: signedOut$,
    profileType: profileType$,
}).map(
    ({ signedOut, profileType }) =>
        signedOut || (profileType != null && !isChildProfile(profileType))
)

/**
 * The age-band restriction reason for the active profile, when any — feeds
 * R33's explicit-playback fork. Only `CHILD_AGE_RESTRICTED` is derivable
 * client-side; a teen band is server-only and never synthesized here.
 */
export const ageRestrictedReason$: ObservableValue<Maybe<string>> = isChildProfile$.map((child) =>
    child ? 'CHILD_AGE_RESTRICTED' : undefined
)

// Explicit-language-filter settings capability. GH reads
// `capabilities.updateUserExplicitLanguageFilter.isAllowed !== false`; amu's Firefly
// gateway does not yet serve it, so we synthesize the capability from the
// active profile's age band the same way GH's type policies mock the
// child-profile capabilities. Kept beside the other child gates because its
// interim source is `profileType`.
export interface SetExplicitLanguageFilterCapability {
    readonly isAllowed?: boolean | null
}

/**
 * Fail-open capability contract: an absent capability or any `isAllowed`
 * other than an explicit `false` means the customer MAY change the filter.
 * Mirrors GH's `canSetExplicitLanguageFilter`.
 */
export function canSetExplicitLanguageFilter(
    cap: Maybe<SetExplicitLanguageFilterCapability>
): boolean {
    return cap?.isAllowed !== false
}

/**
 * Whether the explicit-filter settings toggle should be shown. A child
 * profile has the filter locked by the account holder (`isAllowed: false`),
 * so the toggle is hidden; a non-child sees it. Fails SAFE until the profile
 * resolves so a loading child can never flip the parent's filter off.
 */
export const canSetExplicitLanguageFilter$: ObservableValue<boolean> = profileType$.map(
    (t) => t != null && canSetExplicitLanguageFilter({ isAllowed: !isChildProfile(t) })
)
